import React from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Plus, ChevronRight, Trash2
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    stepKindLabel
} from './copy';
import {
    STEP_KINDS
} from './stepKindCatalog';
import OpcodeEmptyStateCTAs from './OpcodeEmptyStateCTAs';
import SimpleTypedInputsEditor from './components/SimpleTypedInputsEditor';
import SimpleTypedOutputsEditor from './components/SimpleTypedOutputsEditor';

export function OverviewSection({
    title,
    hint,
    children
}) {
    return (
        <section className="min-w-0 border-b border-foreground/20 py-2.5 last:border-b-0">
            <div className="mb-2 pb-1">
                <div className="text-sm font-semibold">{title}</div>
                {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            </div>
            {children}
        </section>
    );
}

export function ProcedureStepList({
    steps,
    selectedStepIdx,
    onSelectStep,
    onAddStep,
    onRemoveStep,
    onStartRagAnswer,
    onStartApiSummarize,
    onStartLlmOnly,
    addButtonLabel = 'Add step',
    emptyMessage = 'No steps yet — pick a starter below or add a step.',
    showEmptyCTAs = true
}) {
    const headClass = 'h-6 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';
    const cellClass = 'px-2 py-1';

    return (
        <div className="space-y-1.5 text-xs">
            {steps.length === 0 ? (
                <>
                    {emptyMessage && (
                        <div className="rounded-sm border border-foreground/20 px-3 py-5 text-center text-xs text-muted-foreground">
                            {emptyMessage}
                        </div>
                    )}
                    {showEmptyCTAs && (
                        <OpcodeEmptyStateCTAs
                            compact
                            onStartRagAnswer={onStartRagAnswer}
                            onStartApiSummarize={onStartApiSummarize}
                            onStartLlmOnly={onStartLlmOnly}
                        />
                    )}
                </>
            ) : (
                <div className="overflow-hidden rounded-sm border border-foreground/20">
                    <Table className="table-fixed w-full">
                        <colgroup>
                            <col className="w-7" />
                            <col className="w-[24%]" />
                            <col className="w-[30%]" />
                            <col className="w-[26%]" />
                            <col className="w-12" />
                        </colgroup>
                        <TableHeader>
                            <TableRow className="border-foreground/20 hover:bg-transparent">
                                <TableHead className={cn(headClass, 'w-7 text-center')}>#</TableHead>
                                <TableHead className={headClass}>Kind</TableHead>
                                <TableHead className={headClass}>Step</TableHead>
                                <TableHead className={headClass}>ID</TableHead>
                                <TableHead className={cn(headClass, 'w-12 text-center')} aria-hidden />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {steps.map((s, i) => {
                                const kind = s.kind || 'llm';
                                const meta = STEP_KINDS.find((k) => k.kind === kind);
                                const technical = stepKindLabel(kind, 'advanced');
                                const label = (s.description && String(s.description).trim())
                                    || stepKindLabel(kind, 'simple');
                                const stepId = s.id || `step_${i + 1}`;
                                const sel = selectedStepIdx === i;
                                return (
                                    <TableRow
                                        key={s.id || i}
                                        role="button"
                                        tabIndex={0}
                                        className={cn(
                                            'cursor-pointer border-foreground/15 transition-colors',
                                            sel
                                                ? 'bg-muted/70 shadow-[inset_0_0_0_2px_hsl(var(--primary))] hover:bg-muted/70'
                                                : 'hover:bg-muted/35'
                                        )}
                                        onClick={() => onSelectStep(i)}
                                        title="Open step configuration"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSelectStep(i);
                                            }
                                        }}
                                    >
                                        <TableCell className={cn(cellClass, 'text-center font-mono text-[11px] text-muted-foreground')}>
                                            {i + 1}
                                        </TableCell>
                                        <TableCell className={cellClass}>
                                            <span
                                                className={cn(
                                                    'inline-flex rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                                                    meta?.bgColor || 'bg-muted',
                                                    meta?.color || 'text-foreground'
                                                )}
                                            >
                                                {technical}
                                            </span>
                                        </TableCell>
                                        <TableCell className={cn(cellClass, 'truncate text-sm leading-snug', sel && 'font-medium')} title={label}>
                                            {label}
                                        </TableCell>
                                        <TableCell
                                            className={cn(cellClass, 'truncate font-mono text-[10px] text-muted-foreground')}
                                            title={`Reference in expressions as @${stepId}`}
                                        >
                                            {stepId}
                                        </TableCell>
                                        <TableCell className={cn(cellClass, 'text-center')}>
                                            <div className="flex items-center justify-center gap-0.5">
                                                {onRemoveStep && (
                                                    <button
                                                        type="button"
                                                        className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                                                        title="Remove step"
                                                        aria-label={`Remove step ${i + 1}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRemoveStep(i);
                                                        }}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                <ChevronRight
                                                    className={cn(
                                                        'h-4 w-4',
                                                        sel ? 'text-primary' : 'text-muted-foreground/70'
                                                    )}
                                                    aria-hidden
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
            {onAddStep && (
                <Button
                    variant="secondary"
                    size="sm"
                    className="h-7 border border-primary/35 bg-primary/15 text-xs font-semibold text-primary shadow-none hover:bg-primary/25"
                    onClick={onAddStep}
                >
                    <Plus className="mr-1 h-3 w-3" /> {addButtonLabel}
                </Button>
            )}
        </div>
    );
}

function FlowList({
    flows,
    onSelectFlow,
    onAddFlow,
    onRemoveFlow
}) {
    const headClass = 'h-6 px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';
    const cellClass = 'px-2 py-1';

    return (
        <div className="space-y-1.5 text-xs">
            {flows.length > 0 && (
                <div className="overflow-hidden rounded-sm border border-foreground/20">
                    <Table className="table-fixed w-full">
                        <colgroup>
                            <col className="w-2/3" />
                            <col className="w-1/3" />
                            <col className="w-8" />
                        </colgroup>
                        <TableHeader>
                            <TableRow className="border-foreground/20 hover:bg-transparent">
                                <TableHead className={headClass}>Flow</TableHead>
                                <TableHead className={cn(headClass, 'text-center')}>Steps</TableHead>
                                <TableHead className={cn(headClass, 'w-8 text-center')} aria-hidden />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {flows.map((f, i) => {
                                const stepCount = (f.steps || []).length;
                                return (
                                    <TableRow
                                        key={f.id || i}
                                        role="button"
                                        tabIndex={0}
                                        className="cursor-pointer border-foreground/15 transition-colors hover:bg-muted/35"
                                        onClick={() => onSelectFlow(i)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSelectFlow(i);
                                            }
                                        }}
                                    >
                                        <TableCell className={cn(cellClass, 'font-mono text-sm')}>
                                            {f.id || `flow_${i + 1}`}
                                        </TableCell>
                                        <TableCell className={cn(cellClass, 'text-center text-muted-foreground')}>
                                            {stepCount}
                                        </TableCell>
                                        <TableCell className={cn(cellClass, 'text-center')}>
                                            <button
                                                type="button"
                                                className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                                                title="Remove flow"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemoveFlow(i);
                                                }}
                                            >
                                                <Trash2 className="mx-auto h-3.5 w-3.5" />
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
            <Button
                variant="secondary"
                size="sm"
                className="h-7 border border-primary/35 bg-primary/15 text-xs font-semibold text-primary shadow-none hover:bg-primary/25"
                onClick={onAddFlow}
            >
                <Plus className="mr-1 h-3 w-3" /> Add flow
            </Button>
        </div>
    );
}

/**
 * Simple-mode left column: inputs, procedure, flows list, definitions, output.
 */
export default function OpcodeSimpleLeftPanel({
    inputs,
    onInputsChange,
    outputs,
    onOutputsChange,
    outputsOelContext,
    steps,
    selectedStepIdx,
    onSelectStep,
    onAddStep,
    onRemoveStep,
    onStartRagAnswer,
    onStartApiSummarize,
    onStartLlmOnly,
    flows,
    onSelectFlow,
    onAddFlow,
    onRemoveFlow,
    definitions,
    selectedDefinitionIdx,
    onSelectDefinition,
    onAddDefinition,
    onRemoveDefinition
}) {
    const stepCount = (steps || []).length;
    const flowCount = (flows || []).length;
    const defCount = (definitions || []).length;

    return (
        <div className="border-t border-foreground/20">
            <OverviewSection title="Inputs" hint="Passed in at runtime">
                <SimpleTypedInputsEditor
                    inputs={inputs}
                    onChange={onInputsChange}
                    embedded
                />
            </OverviewSection>

            <OverviewSection
                title="Procedure"
                hint={stepCount
                    ? `${stepCount} step${stepCount === 1 ? '' : 's'} — select one to configure on the right`
                    : 'Add steps, then configure them on the right'}
            >
                <ProcedureStepList
                    steps={steps}
                    selectedStepIdx={selectedStepIdx}
                    onSelectStep={onSelectStep}
                    onAddStep={onAddStep}
                    onRemoveStep={onRemoveStep}
                    onStartRagAnswer={onStartRagAnswer}
                    onStartApiSummarize={onStartApiSummarize}
                    onStartLlmOnly={onStartLlmOnly}
                />
            </OverviewSection>

            <OverviewSection
                title="Flows"
                hint={flowCount
                    ? `${flowCount} flow${flowCount === 1 ? '' : 's'} — reusable sub-workflows`
                    : 'Named step sequences for Subflow and Choose steps'}
            >
                <FlowList
                    flows={flows}
                    onSelectFlow={onSelectFlow}
                    onAddFlow={onAddFlow}
                    onRemoveFlow={onRemoveFlow}
                />
            </OverviewSection>

            <OverviewSection
                title="Definitions"
                hint={defCount
                    ? `${defCount} definition${defCount === 1 ? '' : 's'} — reusable single steps`
                    : 'Reusable steps for Call and Choose'}
            >
                <ProcedureStepList
                    steps={definitions}
                    selectedStepIdx={selectedDefinitionIdx}
                    onSelectStep={onSelectDefinition}
                    onAddStep={onAddDefinition}
                    onRemoveStep={onRemoveDefinition}
                    showEmptyCTAs={false}
                    emptyMessage=""
                    addButtonLabel="Add definition"
                />
            </OverviewSection>

            <OverviewSection title="Output" hint="From step results">
                {stepCount === 0 ? (
                    <div className="px-1 py-2 text-xs leading-snug text-muted-foreground">
                        Add procedure steps first, then map outputs here.
                    </div>
                ) : (
                    <SimpleTypedOutputsEditor
                        outputs={outputs}
                        onChange={onOutputsChange}
                        embedded
                        oelContext={outputsOelContext}
                    />
                )}
            </OverviewSection>
        </div>
    );
}
